import { ROLE_LABELS } from '@kominiarz/shared';
import { LogOut, Plus } from 'lucide-react';
import { useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useCurrentUser, useLogout } from '../auth/useAuth';
import { BrandMark } from '../components/BrandMark';
import { ColorDot } from '../components/ColorDot';
import { useLayoutMode } from '../lib/breakpoints';
import { useBackgroundSync } from '../lib/useBackgroundSync';
import { StatusBanner } from '../components/StatusBanner';
import { MORE_NAV, NEW_VISIT_PATH, PRIMARY_NAV, SECONDARY_NAV, type NavItem } from './navigation';
import styles from './AppShell.module.css';

function navClass({ isActive }: { isActive: boolean }) {
  return isActive ? `${styles.navItem} ${styles.active}` : styles.navItem;
}

/** Which main tab a screen belongs to (a visit opened from the calendar keeps "Kalendarz" lit). */
function tabFor(pathname: string): string | null {
  if (pathname.startsWith('/kalendarz') || pathname.startsWith('/wizyty')) return '/kalendarz';
  if (pathname.startsWith('/do-umowienia')) return '/do-umowienia';
  if (pathname.startsWith('/klienci')) return '/klienci';
  if (pathname.startsWith('/wiecej') || SECONDARY_NAV.some((item) => pathname.startsWith(item.to))) return '/wiecej';
  return null;
}

function NavEntry({ item, current }: { item: NavItem; current?: string | null }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      className={current === undefined ? navClass : () => navClass({ isActive: current === item.to })}
    >
      <Icon aria-hidden="true" className={styles.navIcon} />
      <span className={styles.navLabel}>{item.label}</span>
    </NavLink>
  );
}

/** Pages where the floating "Nowa wizyta" button would be in the way. */
function hidesNewVisitButton(pathname: string) {
  return pathname.startsWith('/wizyty/');
}

/**
 * Full-screen forms on phones hide the tab bar (like system apps do), so the form and
 * its save button get the whole screen; "Wróć" in the app bar leads out.
 */
const FORM_ROUTES = [
  /^\/wizyty\/nowa$/,
  /^\/wizyty\/\d+\/edycja$/,
  /^\/klienci\/nowy$/,
  /^\/klienci\/\d+\/edycja$/,
  /^\/uzytkownicy\/(nowy|\d+)$/,
  /^\/ustawienia\/haslo$/,
];
const isFormRoute = (pathname: string) => FORM_ROUTES.some((r) => r.test(pathname));

export function AppShell() {
  const mode = useLayoutMode();
  const { pathname } = useLocation();
  const user = useCurrentUser();
  const logout = useLogout();
  useBackgroundSync(user.id);
  const secondary = SECONDARY_NAV.filter((item) => !item.adminOnly || user.role === 'admin');
  const showTabbar = mode === 'phone' && !isFormRoute(pathname);
  const showFab = showTabbar && !hidesNewVisitButton(pathname);

  // Fixed and sticky elements (save bars, toasts, the floating button) keep clear of the tab bar.
  useEffect(() => {
    const root = document.documentElement;
    if (showTabbar) root.dataset.tabbar = 'shown';
    else delete root.dataset.tabbar;
    return () => {
      delete root.dataset.tabbar;
    };
  }, [showTabbar]);

  if (mode === 'phone') {
    return (
      <div className={showFab ? `${styles.phone} ${styles.withFab}` : styles.phone}>
        <main className={styles.main}>
          <StatusBanner sticky />
          <Outlet />
        </main>
        {showFab && (
          <NavLink to={NEW_VISIT_PATH} className={styles.fab}>
            <Plus aria-hidden="true" />
            <span>Nowa wizyta</span>
          </NavLink>
        )}
        {showTabbar && (
          <nav className={styles.tabbar} aria-label="Nawigacja główna">
            {[...PRIMARY_NAV, MORE_NAV].map((item) => (
              <NavEntry key={item.to} item={item} current={tabFor(pathname)} />
            ))}
          </nav>
        )}
      </div>
    );
  }

  const isDesktop = mode === 'desktop';
  return (
    <div className={isDesktop ? styles.desktop : styles.tablet}>
      <nav className={styles.side} aria-label="Nawigacja główna">
        <div className={styles.brand}>
          <BrandMark size={isDesktop ? 40 : 44} />
          {isDesktop && <span className={styles.brandName}>Kalendarz wizyt</span>}
        </div>
        <NavLink to={NEW_VISIT_PATH} className={styles.newVisit}>
          <Plus aria-hidden="true" />
          <span>Nowa wizyta</span>
        </NavLink>
        <div className={styles.navGroup}>
          {PRIMARY_NAV.map((item) => (
            <NavEntry key={item.to} item={item} current={isDesktop ? undefined : tabFor(pathname)} />
          ))}
          {!isDesktop && <NavEntry item={MORE_NAV} current={tabFor(pathname)} />}
        </div>
        {isDesktop && (
          <div className={styles.navGroup}>
            {secondary.map((item) => (
              <NavEntry key={item.to} item={item} />
            ))}
          </div>
        )}
        {isDesktop && (
          <div className={styles.userBlock}>
            <div className={styles.userInfo}>
              <ColorDot color={user.color} />
              <span className={styles.userText}>
                <span className={styles.userName}>{user.name}</span>
                <span className={styles.userRole}>{ROLE_LABELS[user.role]}</span>
              </span>
            </div>
            <button type="button" className={styles.navItem} onClick={() => logout.mutate()}>
              <LogOut aria-hidden="true" className={styles.navIcon} />
              <span className={styles.navLabel}>Wyloguj się</span>
            </button>
          </div>
        )}
      </nav>
      <main className={styles.main}>
        <StatusBanner sticky />
        <Outlet />
      </main>
    </div>
  );
}
